export const HOUR_HEIGHT = 80;

export function getHourLabel(hour: number): string {
  return `${hour.toString().padStart(2, '0')}:00`;
}

export function parseOffsetMinutes(offset: string): number {
  const match = offset.match(/^([+-])(\d{2}):?(\d{2})$/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
}

export function toLocalYMD(date: Date, offsetMinutes: number): string {
  return new Date(date.getTime() + offsetMinutes * 60_000)
    .toISOString()
    .slice(0, 10);
}

export function buildDays(
  startsAt: Date,
  endsAt: Date,
  offsetMinutes: number,
): Array<{ date: string; label: string; shortLabel: string }> {
  const startYMD = toLocalYMD(startsAt, offsetMinutes);
  const endYMD = toLocalYMD(endsAt, offsetMinutes);
  const longFmt = new Intl.DateTimeFormat('es-CL', {
    weekday: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const shortFmt = new Intl.DateTimeFormat('es-CL', {
    weekday: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const days: Array<{ date: string; label: string; shortLabel: string }> = [];
  const cursor = new Date(`${startYMD}T00:00:00Z`);
  const end = new Date(`${endYMD}T00:00:00Z`);
  while (cursor.getTime() <= end.getTime()) {
    const ymd = cursor.toISOString().slice(0, 10);
    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    days.push({
      date: ymd,
      label: cap(longFmt.format(cursor)),
      shortLabel: cap(shortFmt.format(cursor)).replace('.', ''),
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

export function calculatePosition(date: Date, dayStart: Date): number {
  const diff = date.getTime() - dayStart.getTime();
  const hours = diff / (1000 * 60 * 60);
  return hours * HOUR_HEIGHT;
}

export interface SlotColumn<T> {
  position: number;
  slots: T[];
  slotWidth: number;
}

export function buildSlotColumns<T extends { startTime: Date | string }>(
  slots: T[],
  dayStart: Date,
): Array<SlotColumn<T>> {
  const slotsByStartTime = new Map<number, T[]>();
  slots.forEach((slot) => {
    const position = calculatePosition(new Date(slot.startTime), dayStart);
    if (!slotsByStartTime.has(position)) {
      slotsByStartTime.set(position, []);
    }
    slotsByStartTime.get(position)?.push(slot);
  });

  return Array.from(slotsByStartTime.entries()).map(
    ([position, overlappingSlots]) => ({
      position,
      slots: overlappingSlots,
      slotWidth: 100 / overlappingSlots.length,
    }),
  );
}
